import type { ImmutableArray } from "shelving";
import { getMediaURL } from "shared/util/media";
import { useDialog } from "../base/Dialogs.js";

type MediaImageProps = {
	path: string;
	title?: string | undefined;
	mods?: string | undefined;
};

/** Show a media image. */
export function MediaImage({ path, title, mods }: MediaImageProps): React.ReactElement {
	return <img src={getMediaURL(path)} alt={title} title={title} className={mods} loading="lazy" draggable="false" />;
}

/** Show a media image as an icon. */
export function MediaIcon({ path, title, mods = "" }: MediaImageProps): React.ReactElement {
	return <img src={getMediaURL(path, "icon")} alt={title} title={title} className={`icon ${mods}`} loading="lazy" draggable="false" />;
}

/** Show a media image as a photo. */
export function MediaPhoto({ path, title, mods = "" }: MediaImageProps): React.ReactElement {
	return <img src={getMediaURL(path, "photo")} alt={title} title={title} className={`photo ${mods}`} loading="lazy" draggable="false" />;
}

/** Show a media image as a photo that can be expanded on click. */
export function MediaThumb({ path, title, mods = "" }: MediaImageProps): React.ReactElement {
	const showDialog = useDialog();
	return (
		<a
			href={getMediaURL(path)}
			className={`photo ${mods}`}
			onClick={e => {
				e.preventDefault(); // Stop navigation.
				showDialog(<MediaImage path={path} title={title} mods="pin-center" />); // Show a dialog instead.
			}}
		>
			<MediaPhoto path={path} />
		</a>
	);
}

/** Show a set of photos from the media bucket in Cloud Storage that expand on click. */
export function MediaThumbs({ mods = "flex-row space-xsmall flex-left gap-xsmall", paths }: { mods?: string | undefined; paths: ImmutableArray<string> }): React.ReactElement {
	return (
		<p className={mods}>
			{paths.map(image => (
				<MediaThumb key={image} path={image} />
			))}
		</p>
	);
}
